import * as React from 'react';
import { SingleValueComponent } from '../common';
import styles from './display-boolean.scss';
import { compose } from 'redux';
import { Label } from 'ui/common';

export interface DisplayBooleanProps extends SingleValueComponent<number>{
  /**
   * Display modes defined by design handbook
   */
  style?: 'secondary';
  /**
   * When true, a predefined style class to render the label above the value is applied
   */
  showStacked: boolean;
  /**
   * When true, the label font is normal and the value font is bold
   */
  strong?: boolean; 
  /**
   * When true, the component gets hidden when value is empty
   */
  hideIfNoValue?: boolean;
  /**
   * translation text if the value is selected
   */
  textCheckedValue?: string;
  /**
   * translation text if the value is not selected
   */
  textUncheckedValue?: string;

  /**
   * @internal
   */
  children?: any;
}

/**
 * A utility that maps known props to a known style and appends them to a list.
 * 
 * The list of final style classes are concatenated into a valid JSX className string attribute
 * 
 * @param props 
 */
const styleFromProps = (props: DisplayBooleanProps) =>
  [
    styles.labelAndBoolean,
    props.className,
    props.strong && styles.strong,
    props.showStacked && styles.stackedColumn,
    !!styles[props.style] && styles[props.style]    
  ].filter(s => s).join(' ');

const DisplayBooleanComponent = (props: DisplayBooleanProps) => {
  const {hideIfNoValue, label, value, textCheckedValue, textUncheckedValue} = props;
  const hideComponent = hideIfNoValue && !value;
  const valueText = !!value ? !!textCheckedValue && textCheckedValue
                            : !!textUncheckedValue && textUncheckedValue;
  return(
    !hideComponent &&
    <div id={props.componentId} className={`${props.className} ${styles.labelAndBoolean}`}>
      <Label label={label} />
      <span className={'value-boolean'}>
        {valueText}
      </span>
    </div> 
  );
};

/**
 * Higher order component to override the final className
 * @param Component the component to decorate
 */
const styleVariations = (Component: any) => ({children, ...childProps}: DisplayBooleanProps) => (
  <Component {...childProps} className={styleFromProps(childProps)}>
    {children}
  </Component>
);

export const DisplayBoolean = compose(styleVariations)(DisplayBooleanComponent);
